import React, { useState } from 'react';
import CustomReports from './CustomReports';
import Reports from './Reports';
import DefaultReportView from './DefaultReportView';
import { DEFAULT_ANALYTICS_REPORTS } from '../config/analyticsReports';
import { FileText } from 'lucide-react';
import { HubSection, HubTabs } from './ui/HubShell';

type AnalyticsTab = 'device-analytics' | 'reports';
type ReportsSubTab = 'default-list' | 'policy-query';

const TABS: { id: AnalyticsTab; label: string }[] = [
  { id: 'device-analytics', label: 'Device Analytics' },
  { id: 'reports', label: 'Reports' },
];

const AnalyticsHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('device-analytics');
  const [reportsSubTab, setReportsSubTab] = useState<ReportsSubTab>('default-list');
  const [selectedReport, setSelectedReport] = useState<(typeof DEFAULT_ANALYTICS_REPORTS)[0] | null>(null);

  return (
    <HubSection
      title="Analytics"
      subtitle="Default reports, device analytics, and policy reports."
      icon={<FileText size={20} />}
      eyebrow="Insights"
      tabs={TABS}
      tab={activeTab}
      onTabChange={(id) => {
        setActiveTab(id);
        setSelectedReport(null);
        if (id === 'reports') setReportsSubTab('default-list');
      }}
    >
      {activeTab === 'device-analytics' && <CustomReports />}

      {activeTab === 'reports' && (
        <>
          {selectedReport ? (
            <div>
              <button
                type="button"
                onClick={() => setSelectedReport(null)}
                className="mb-4 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium"
              >
                ← Back to reports
              </button>
              <DefaultReportView
                reportId={selectedReport.reportId}
                reportName={selectedReport.name}
                description={selectedReport.description}
              />
            </div>
          ) : (
            <>
              <HubTabs
                variant="segment"
                className="!mx-0 mb-6"
                value={reportsSubTab}
                onChange={setReportsSubTab}
                items={[
                  { id: 'default-list', label: 'Default reports' },
                  { id: 'policy-query', label: 'Policy & custom query' },
                ]}
              />
              {reportsSubTab === 'default-list' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {DEFAULT_ANALYTICS_REPORTS.map((report) => (
                    <button
                      key={report.id}
                      type="button"
                      onClick={() => setSelectedReport(report)}
                      className="p-5 rounded-lg border-2 border-slate-200 bg-white text-left hover:shadow-lg hover:border-teal-200 transition-all"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-teal-700">
                          <FileText size={20} />
                        </div>
                        <span className="font-semibold text-slate-800">{report.name}</span>
                      </div>
                      <p className="text-sm text-slate-600">{report.description}</p>
                    </button>
                  ))}
                </div>
              )}
              {reportsSubTab === 'policy-query' && <Reports />}
            </>
          )}
        </>
      )}
    </HubSection>
  );
};

export default AnalyticsHub;
